import { useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useUpdateFile } from 'hooks/queries/files/useUpdateFile'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { showToast } from 'common/ToastMessage'
import { ToastMessage } from 'common/ToastMessage/ShowToastMessage'
import dayjs from 'dayjs'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'

type ApprovalState = 'pending' | 'approved' | 'rejected' | 'posted' | 'draft' | null

export const getApprovalState = (almanacFile?: AlmanacFileShowItem): ApprovalState => {
  if (!almanacFile) return null
  if (almanacFile.reviewed_at && almanacFile.post_status === PostStatusPropertyValue.AutoPostScheduled) {
    return 'approved'
  }
  if (almanacFile.reviewed_at && almanacFile.post_status === PostStatusPropertyValue.Draft) {
    return 'rejected'
  }
  if (almanacFile.post_status === PostStatusPropertyValue.Posted) {
    return 'posted'
  }
  if (!almanacFile.reviewed_at && almanacFile.post_at) {
    return 'pending'
  }
  return 'draft'
}

interface UseApprovalActionsProps {
  almanacFile?: AlmanacFileShowItem
  onUpdateAlmanacFile?: (updatedFile: AlmanacFileShowItem) => void
  onDelete: (skipNavigation?: boolean) => void
  setIsLoading: (isLoading: boolean) => void
}

export const useApprovalActions = ({
  almanacFile,
  onUpdateAlmanacFile,
  onDelete,
  setIsLoading,
}: UseApprovalActionsProps) => {
  const queryClient = useQueryClient()
  const currentWorkspace = useCurrentWorkspace()
  const originalPostAt = useRef(almanacFile?.post_at)

  const updateFileMutation = useUpdateFile(almanacFile?.key || '', {
    onSuccess: (updatedFile) => {
      onUpdateAlmanacFile?.(updatedFile as AlmanacFileShowItem)
      queryClient.invalidateQueries(filesKeys.partialCalendar(currentWorkspace?.id))
      queryClient.invalidateQueries(['workspaces', currentWorkspace?.id, 'schedules'])
    },
  })

  const handleUndoDontPost = async () => {
    setIsLoading(true)
    try {
      await updateFileMutation.mutateAsync({
        post_at: originalPostAt.current,
        post_status: PostStatusPropertyValue.Draft,
        reviewed_at: null,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleApprove = async () => {
    setIsLoading(true)
    try {
      await updateFileMutation.mutateAsync({
        post_status: PostStatusPropertyValue.AutoPostScheduled,
        reviewed_at: dayjs().toISOString(),
      })
    } catch {
      showToastMessage('Failed to approve post', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleMoveToDraft = async () => {
    if (!almanacFile) return
    setIsLoading(true)
    try {
      await updateFileMutation.mutateAsync({
        post_status: PostStatusPropertyValue.Draft,
        reviewed_at: null,
      })
    } catch {
      showToastMessage('Failed to move back to draft', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDontPost = async () => {
    setIsLoading(true)
    try {
      await updateFileMutation.mutateAsync({
        post_at: null,
        post_status: PostStatusPropertyValue.Draft,
        reviewed_at: dayjs().toISOString(),
      })
      onDelete(true)
      showToast(
        <ToastMessage
          toastMessage="Post moved to drafts"
          buttonProps={{
            label: 'Undo',
            onPress: handleUndoDontPost,
            variant: 'cancel',
            type: 'button',
          }}
        />,
        3,
      )
    } catch {
      showToastMessage('Failed to cancel post', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const approvalState = getApprovalState(almanacFile)
  const isPostApprovalsEnabled = currentWorkspace?.post_approval_enabled
  const showReviewButtons = isPostApprovalsEnabled && approvalState === 'pending'
  const showApprovedDate = isPostApprovalsEnabled && approvalState === 'approved'
  const approvedDate = almanacFile?.reviewed_at ? dayjs(almanacFile.reviewed_at).format('MMM D') : null

  return {
    handleApprove,
    handleDontPost,
    handleMoveToDraft,
    showReviewButtons,
    showApprovedDate,
    approvedDate,
  }
}
